import { useState, useEffect } from 'react'
import { Space, Typography, Image, Form, Select, Button, App, Spin } from 'antd'
import ReactJson from '@microlink/react-json-view'
import dayjs from 'dayjs'
import type { FeedbackWithNotes, FeedbackStatus, FeedbackPriority, FeedbackCategory } from '@/types/feedback.types'
import { FeedbackNoteThread } from './FeedbackNoteThread'
import { feedbackService } from '@/services/feedbackService'

const { Text } = Typography

/**
 * Props for FeedbackExpandedRow component
 */
export interface FeedbackExpandedRowProps {
  record: FeedbackWithNotes
  isAdminView: boolean
  onUpdate?: () => void
  notesLoading?: boolean
}

/**
 * FeedbackExpandedRow Component
 *
 * Expanded row content matching Audit Logs pattern.
 * Shows full details, screenshot, error context, notes, and admin actions.
 */
export const FeedbackExpandedRow: React.FC<FeedbackExpandedRowProps> = ({
  record,
  isAdminView,
  onUpdate,
  notesLoading,
}) => {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null)
  const [attachmentLoading, setAttachmentLoading] = useState(!!record.attachmentKey)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)

  // Fetch presigned URL for attachment when component mounts
  useEffect(() => {
    if (record.attachmentKey) {
      setAttachmentLoading(true)
      setAttachmentError(null)
      setAttachmentUrl(null)

      console.log('[Attachment] Fetching presigned URL for:', record.attachmentKey)

      feedbackService.getAttachmentUrl(record.attachmentKey).then((result) => {
        console.log('[Attachment] API response:', result)
        if (result.success && result.data?.viewUrl) {
          setAttachmentUrl(result.data.viewUrl)
        } else {
          const errMsg = result.message || result.errors?.[0]?.message || 'Failed to load attachment URL'
          console.error('[Attachment] Error:', errMsg)
          setAttachmentError(errMsg)
        }
        setAttachmentLoading(false)
      }).catch((err) => {
        console.error('[Attachment] Exception:', err)
        setAttachmentError(err.message || 'Failed to load attachment')
        setAttachmentLoading(false)
      })
    }
  }, [record.attachmentKey])

  const statusOptions: FeedbackStatus[] = ['New', 'In Progress', 'Duplicate', 'Resolved']
  const priorityOptions: FeedbackPriority[] = ['Low', 'Medium', 'High', 'Critical']
  const categoryOptions: FeedbackCategory[] = [
    'Pricing',
    'Reports',
    'Data Import',
    'Connections',
    'User Interface',
    'Performance',
    'Other',
  ]

  const handleSave = async () => {
    const values = form.getFieldsValue()
    setSaving(true)

    const result = await feedbackService.updateFeedback({
      uid: record.uid,
      version: record.version,
      status: values.status,
      priority: values.priority,
      category: values.category,
    })

    setSaving(false)

    if (!result.success) {
      message.error('Failed to update feedback')
      return
    }

    message.success('Feedback updated successfully')
    onUpdate?.()
  }

  return (
    <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Full Description */}
        <div>
          <Text strong>Full Description</Text>
          <div style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>
            {record.feedbackComments || '—'}
          </div>
        </div>

        {/* Screenshot */}
        {record.attachmentKey && (
          <div>
            <Text strong>Screenshot</Text>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              ({record.attachmentName || 'attachment'})
            </Text>
            <div style={{ marginTop: '8px' }}>
              {attachmentLoading ? (
                <Spin tip="Loading attachment..." />
              ) : attachmentUrl ? (
                <Image
                  src={attachmentUrl}
                  alt={record.attachmentName || 'Screenshot'}
                  style={{ maxWidth: '400px' }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                />
              ) : attachmentError ? (
                <Text type="danger">{attachmentError}</Text>
              ) : (
                <Text type="secondary">Unable to load attachment</Text>
              )}
            </div>
          </div>
        )}

        {/* Context & Error Details (only for Error type) */}
        {record.feedbackType === 'Error' && (
          <>
            {/* Context */}
            <div>
              <Text strong>Context</Text>
              <div style={{ marginTop: '8px' }}>
                {record.pageUrl && (
                  <div>
                    <Text type="secondary">Page: </Text>
                    <Text>{record.pageUrl}</Text>
                  </div>
                )}
                {record.browserInfo && (
                  <div>
                    <Text type="secondary">Browser: </Text>
                    <Text>{record.browserInfo}</Text>
                  </div>
                )}
                <div>
                  <Text type="secondary">Submitted: </Text>
                  <Text>{dayjs(record.createdAt).format('MMM D, YYYY [at] h:mm A')}</Text>
                </div>
              </div>
            </div>

            {/* Error Details */}
            <div>
              <Text strong>Error Details</Text>
              <div style={{ marginTop: '8px' }}>
                {record.eventId && (
                  <div>
                    <Text type="secondary">Event ID: </Text>
                    <Text code>{record.eventId}</Text>
                  </div>
                )}
                {record.errorCode && (
                  <div>
                    <Text type="secondary">Error Code: </Text>
                    <Text code>{record.errorCode}</Text>
                  </div>
                )}
                {record.errorStack && (
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary">Stack Trace:</Text>
                    <div style={{ marginTop: '8px', maxHeight: '300px', overflow: 'auto' }}>
                      <ReactJson
                        src={{ stack: record.errorStack }}
                        theme="rjv-default"
                        collapsed={1}
                        displayDataTypes={false}
                        enableClipboard
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Notes Thread */}
        <FeedbackNoteThread
          feedbackId={record.uid}
          notes={record.notes}
          isAdminView={isAdminView}
          onNoteAdded={onUpdate}
          loading={notesLoading}
        />

        {/* Admin Actions */}
        {isAdminView && (
          <div>
            <Text strong>Admin Actions</Text>
            <Form
              form={form}
              layout="inline"
              style={{ marginTop: '8px' }}
              initialValues={{
                status: record.status,
                priority: record.priority,
                category: record.category,
              }}
            >
              <Form.Item name="status" label="Status">
                <Select
                  style={{ width: 150 }}
                  options={statusOptions.map((s) => ({ label: s, value: s }))}
                />
              </Form.Item>
              <Form.Item name="priority" label="Priority">
                <Select
                  style={{ width: 120 }}
                  options={priorityOptions.map((p) => ({ label: p, value: p }))}
                />
              </Form.Item>
              <Form.Item name="category" label="Category">
                <Select
                  style={{ width: 150 }}
                  options={categoryOptions.map((c) => ({ label: c, value: c }))}
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" onClick={handleSave} loading={saving}>
                  Save
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </Space>
    </div>
  )
}

export default FeedbackExpandedRow
